<template>
  <UIDropdown
    v-if="activeModel"
    ref="dropdownRef"
    :items="featuredItems"
    class="DropdownModel"
    @open="isOpen = true"
    @close="isOpen = false"
    @clickedOption="onSelectFeatured"
  >
    <!-- Inside ui-lib's toggle, which supplies the chevron, focus and Enter / Space /
         ArrowDown. Its field border and fill are dropped below: this one reads as the
         current model, not as a form field. -->
    <template #toggle>
      <UISpinner v-if="isFetching" size="sm" class="DropdownModel__loader" />
      <DropdownModelItem
        v-else
        :key="activeModel.slug"
        :isTopbarDisplay="true"
        :model="activeModel"
        class="DropdownModel__active-item"
      />
    </template>

    <template #before-list>
      <li class="DropdownModel__header" role="presentation">
        <div class="DropdownModel__header-label">Models</div>
        <UITooltip placement="right">
          <template #trigger>
            <UIIcon class="DropdownModel__header-icon" name="tooltip" />
          </template>
          <template #content>
            Switch between different models in <br />EverGPT depending on your
            needs
          </template>
        </UITooltip>
      </li>
    </template>

    <template #option="{ option }">
      <span class="DropdownModel__row" @mouseenter="moreMenuRef?.close()">
        <DropdownModelRow
          :model="modelOf(option.value)"
          :isSelected="option.value === activeModel.slug"
        />
      </span>
    </template>

    <template v-if="moreItems.length" #after-list>
      <!-- Opens on click, Enter / ArrowRight and hover, like the chat input's menus. -->
      <ChatInputOptionsSubmenu
        ref="moreMenuRef"
        class="DropdownModel__more"
        label="More models"
        :items="moreItems"
        @mouseenter="moreMenuRef?.open()"
        @clickedOption="onSelectMore"
      >
        <template #option="{ option }">
          <DropdownModelRow
            :model="modelOf(option.value)"
            :isSelected="option.value === activeModel.slug"
          />
        </template>
      </ChatInputOptionsSubmenu>
    </template>
  </UIDropdown>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue'

import { UIDropdown, UISpinner, UITooltip } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'

import type { IModel } from '@/apiClient/types/model'
import { useModelStore } from '@/store/model'
import { splitModelsForChatDropdown } from '@/utils/chatModelOrder'
import ChatInputOptionsSubmenu from '@/views/components/chat/input/ChatInputOptionsSubmenu.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'

import DropdownModelItem from './DropdownModelItem.vue'
import DropdownModelRow from './DropdownModelRow.vue'

const modelStore = useModelStore()
const { selectableModels, activeModel, isFetching } = storeToRefs(modelStore)
const { setActiveModel } = modelStore

const chatDropdownGroups = computed(() =>
  splitModelsForChatDropdown(selectableModels.value),
)
const featuredModels = computed(() => chatDropdownGroups.value.featured)
const moreModels = computed(() => chatDropdownGroups.value.more)

// ui-lib's rows carry only `label` / `value`; the rich row is looked up by slug.
const toItem = (model: { display_name: string; slug: string }) => ({
  label: model.display_name,
  value: model.slug,
})
const featuredItems = computed(() => featuredModels.value.map(toItem))
const moreItems = computed(() => moreModels.value.map(toItem))
const modelBySlug = computed(() => {
  const bySlug: Record<string, IModel> = {}
  for (const model of selectableModels.value) bySlug[model.slug] = model
  return bySlug
})
// Rows are only ever built from these two lists, so the lookup always finds one.
const modelOf = (slug: string) => modelBySlug.value[slug]

const dropdownRef =
  useTemplateRef<InstanceType<typeof UIDropdown>>('dropdownRef')
const moreMenuRef =
  useTemplateRef<InstanceType<typeof ChatInputOptionsSubmenu>>('moreMenuRef')
const isOpen = ref(false)

const onSelectFeatured = (picked: { value: string }) => {
  const model = modelOf(picked.value)
  if (model) setActiveModel(model)
}

// The submenu's panel is teleported out of this one, so a pick there does not close
// this menu on its own (and a keyboard pick is not a click at all).
const onSelectMore = (picked: { value: string }) => {
  const model = modelOf(picked.value)
  if (!model) return
  // ui-lib's UIDropdown has no close(); its anchor toggles on click.
  if (isOpen.value) dropdownRef.value?.$refs.anchorElement?.click()
  setActiveModel(model)
}
</script>

<style scoped lang="scss">
.DropdownModel {
  :deep(.UIDropdown__trigger) {
    width: 100%;
  }

  :deep(.UIDropdownToggle) {
    height: auto;
    min-width: 0;
    padding-right: var(---fds-global-space-md);
    border-color: transparent;
    border-radius: var(--radius-s);
    background-color: transparent;

    &:hover {
      background-color: var(--bg-secondary);
    }
  }

  .DropdownModel__loader {
    margin: 0 1rem;
  }

  .DropdownModel__active-item {
    padding: 0.5rem 0.5rem 0.5rem 0;
    align-items: center;
  }
}
</style>

<style lang="scss">
// ui-lib teleports the panel to <body> with no per-instance class, so the menu is
// styled globally, keyed on this picker's own row class. The panel is 376px (capped
// to the viewport) and, unlike ui-lib's 286px default, shows up to 25rem of rows
// before it scrolls: each row carries a description. `!important` is what beats the
// inline max-height ui-lib sets on the list.
// The `min-width` ui-lib sets inline (the anchor's width) would beat `max-width` on a
// phone, where the "More models" anchor is the whole row: it is capped here instead.
.UIDropdown__content:has(.DropdownModel__list-item) {
  box-sizing: border-box;
  width: 376px;
  min-width: min(376px, calc(100vw - 1rem)) !important;
  max-width: calc(100vw - 1rem);

  .UIDropdown__content-list {
    max-height: min(25rem, calc(100dvh - 4rem)) !important;
  }

  // Keyboard scrolling keeps the focused row clear of the pinned header and footer.
  .UIDropdown__content-list:has(> .DropdownModel__header) {
    scroll-padding-block: 2.25rem 2.5rem;
  }
}

.UIDropdown__content .UIDropdownListItem:has(.DropdownModel__list-item) {
  min-width: 0;
}

// UITooltip is not teleported; fixed anchors it to the transformed panel so the list cannot clip or scroll it.
.UIDropdown__content:has(.DropdownModel__list-item) .UITooltip {
  position: fixed !important;
}

.DropdownModel__row {
  display: block;
  width: 100%;
  min-width: 0;
}

// ui-lib's `#before-list` / `#after-list` sit inside the scrolling list, so the
// header and the "More models" row are pinned with `sticky`: only the model rows scroll.
.DropdownModel__header {
  position: sticky;
  z-index: 1;
  top: 0;
  background-color: var(--content-bg-color);
  display: flex;
  gap: 0.25rem;
  align-items: center;
  padding: 0.5rem 0.75rem 0.5rem 0.75rem;
  list-style: none;
}

.DropdownModel__header-icon {
  --color: var(--icon-tertiary);
}

.DropdownModel__header-label {
  @include font-size-12px;
  color: var(--text-primary);
  font-weight: 700;
}

// "More models" row: ruled off from the featured rows, like the old footer.
.UIDropdown__content-list > .ChatInputOptionsSubmenu.DropdownModel__more {
  position: sticky;
  z-index: 1;
  bottom: 0;
  // Opaque under the scrolling rows; the row's own hover / open fill painted on top.
  background:
    linear-gradient(var(--bg-color), var(--bg-color)), var(--content-bg-color);
  margin-top: var(---fds-global-space-xxs);
  border-top: 1px solid var(--border-default);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
